import { StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import VisuallyHidden from '@reach/visually-hidden';
import React, { useContext } from 'react';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import { getDateAriaLabel } from '../atomsHelpers';

interface Props {
  returnTextLabelStyles?: StyledProps;
  returnLabelStyles?: StyledProps;
  showReturnText?: boolean;
  returnDate?: string;
  formattedReturnDate?: string;
  /**
   * Date format for the screen readers
   */
  returnDateScreenReader?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMMM, YYYY' | 'MMMM D, YYYY';
  /**
   * Label Key of Return Info
   */
  returnText?: 'cmp-label--return-date' | 'cmp-label--return-date-colon';
}

/**
 * ## Example
 * ```js
<FareItem
  tileBodyPadding="3"
  tileFooterPaddingTop="5"
  tileFooterTextAlign="center"
  fareItemReturnInfo={{
    fontSize:"2",
    marginTop:"3",
    showDepartureInfo:true,
    showDepartureText:true,
    showDepartureDate:true,
    showRouteOnDepartureDate:false,
    departureText:"Departing",
    formattedDepartureDate:"Tue Dec 18, 2018-Tue Jan 22, 2019",
    origin:"LGA",
    destination:"PEK"
  }}
/> 
 * ```
 */
function FareItemReturnInfo({
  returnTextLabelStyles,
  returnLabelStyles,
  showReturnText = false,
  returnDate = '',
  formattedReturnDate = '',
  returnDateScreenReader = 'MMMM D, YYYY',
  returnText = 'cmp-label--return-date',
  ...props
}: StyledProps<Props>) {
  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  return returnDate && formattedReturnDate ? (
    <Box className={`fare-item-return-info ${className}`} style={style}>
      <Text className="return-text" {...returnLabelStyles}>
        {showReturnText && (
          <Text {...returnTextLabelStyles} floatLeft>
            {translate(returnText, 'Returning')}
          </Text>
        )}
        <time dateTime={returnDate}>
          <Text ariaHidden="true" floatLeft marginLeft={showReturnText ? '1' : '0'}>
            {formattedReturnDate}
          </Text>
          <VisuallyHidden>
            {' '}
            {getDateAriaLabel(returnDate, translate, returnDateScreenReader, localizationData)}
          </VisuallyHidden>
        </time>
      </Text>
    </Box>
  ) : null;
}

FareItemReturnInfo.componentName = 'FareItemReturnInfo';

export default FareItemReturnInfo;
